import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Form,
    Alert,
    Spinner,
} from "react-bootstrap";
import { Document, Page } from "react-pdf";

import "./epaper.css";

const API_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8000";

const EPaper = () => {
    const [papers, setPapers] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [pdfUrl, setPdfUrl] = useState("");
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState("");
    const [numPages, setNumPages] = useState(null);

    /*
    |--------------------------------------------------------------------------
    | FETCH EPAPER LIST + LATEST
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const loadEpapers = async () => {
            setLoading(true);
            try {
                // Fetch list of all papers
                const listRes = await fetch(`${API_URL}/api/epaper`);
                const listData = await listRes.json();
                const list = listData?.data || [];
                setPapers(list);

                // Fetch latest paper date
                const latestRes = await fetch(`${API_URL}/api/epaper/latest`);
                const latestData = await latestRes.json();

                if (latestData?.data?.date) {
                    setSelectedDate(latestData.data.date);
                } else if (list.length > 0) {
                    setSelectedDate(list[0].date);
                }
            } catch (err) {
                console.error("API Error:", err);
                setPdfError("Failed to load ePaper list.");
            } finally {
                setLoading(false);
            }
        };

        loadEpapers();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | FETCH PDF URL WHEN DATE CHANGES
    |--------------------------------------------------------------------------
    */

    const fetchPdfForDate = useCallback(async (date) => {
        if (!date) return;
        setPdfUrl("");
        setPdfError("");
        setNumPages(null);
        setPdfLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/epaper/${date}`);
            const result = await res.json();
            if (result?.success && result?.data?.pdfUrl) {
                setPdfUrl(result.data.pdfUrl);
            } else {
                setPdfError("ePaper not available for selected date.");
            }
        } catch (err) {
            console.error("PDF fetch error:", err);
            setPdfError("ePaper not available for selected date.");
        } finally {
            setPdfLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPdfForDate(selectedDate);
    }, [selectedDate, fetchPdfForDate]);

    /*
    |--------------------------------------------------------------------------
    | DISABLE PRINT / SAVE SHORTCUTS
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const handleKeyDown = (e) => {
            const key = e.key?.toLowerCase();
            if (
                (e.ctrlKey || e.metaKey) &&
                ["p", "s", "c", "u"].includes(key)
            ) {
                e.preventDefault();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    /*
    |--------------------------------------------------------------------------
    | DATE CHANGE HANDLER
    |--------------------------------------------------------------------------
    */

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <Container fluid className="epaper-wrapper">

            {/* 📰 HEADER */}
            <div className="epaper-header">
                <h2>🗞 ePaper Archive</h2>

                <div className="d-flex gap-2 align-items-center flex-wrap">

                    {/* 📅 DATE SELECT */}
                    <Form.Select
                        className="date-select"
                        value={selectedDate}
                        onChange={handleDateChange}
                        disabled={loading || papers.length === 0}
                    >
                        {loading && papers.length === 0 ? (
                            <option>Loading ePaper dates...</option>
                        ) : papers.length > 0 ? (
                            papers.map((p) => (
                                <option key={p.date} value={p.date}>
                                    {p.date}
                                </option>
                            ))
                        ) : (
                            <option>No ePaper available</option>
                        )}
                    </Form.Select>

                </div>
            </div>

            {/* 🚫 INITIAL LOADING */}
            {loading && (
                <Row className="py-5">
                    <Col className="text-center">
                        <Spinner animation="border" />
                        <div className="mt-3">Loading ePaper page...</div>
                    </Col>
                </Row>
            )}

            {/* 📄 VIEWER */}
            {!loading && (
                <Row>
                    <Col
                        md={12}
                        className="viewer-panel"
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        {/* 🔄 PDF LOADING */}
                        {pdfLoading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" />
                                <div className="mt-3">Loading ePaper...</div>
                            </div>
                        )}

                        {/* ❌ PDF ERROR */}
                        {pdfError && !pdfLoading && (
                            <Alert variant="warning" className="mt-3">
                                {pdfError}
                            </Alert>
                        )}

                        {/* ✅ PDF VIEWER via react-pdf */}
                        {pdfUrl && !pdfLoading && !pdfError && (
                            <div className="epaper-pdf-container">
                                <Document
                                    file={pdfUrl}
                                    onLoadSuccess={({ numPages }) =>
                                        setNumPages(numPages)
                                    }
                                    onLoadError={(err) => {
                                        console.error("PDF load error:", err);
                                        setPdfError(
                                            "ePaper not available for selected date."
                                        );
                                    }}
                                    loading={
                                        <div className="text-center py-5">
                                            <Spinner animation="border" />
                                            <div className="mt-3">
                                                Loading PDF...
                                            </div>
                                        </div>
                                    }
                                >
                                    {numPages &&
                                        Array.from(
                                            { length: numPages },
                                            (_, i) => (
                                                <div
                                                    key={i + 1}
                                                    className="epaper-page-wrapper"
                                                >
                                                    <Page
                                                        pageNumber={i + 1}
                                                        width={
                                                            Math.min(
                                                                window.innerWidth - 60,
                                                                1100
                                                            )
                                                        }
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={false}
                                                    />
                                                </div>
                                            )
                                        )}
                                </Document>
                            </div>
                        )}

                        {/* 🚫 NO PDF */}
                        {!pdfUrl && !pdfLoading && !pdfError && (
                            <div className="text-center py-5 viewer-message">
                                ePaper not available for selected date
                            </div>
                        )}
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default EPaper;